import { db } from "../firebase/config";
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  onSnapshot,
  getDocs,
  query,
  where
} from "firebase/firestore";

export const getMondayOfDate = (d = new Date()) => {
  const date = new Date(d);
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(date.setDate(diff));
  return monday.toISOString().split("T")[0];
};

export const getSundayOfDate = (d = new Date()) => {
  const mondayStr = getMondayOfDate(d);
  const monday = new Date(mondayStr + "T00:00:00");
  const sunday = new Date(monday.getTime() + 6 * 24 * 60 * 60 * 1000);
  return sunday.toISOString().split("T")[0];
};

export function subscribeToTasks(callback) {
  return onSnapshot(collection(db, "tasks"), (snapshot) => {
    const list = [];
    snapshot.forEach((docSnap) => {
      list.push({ id: docSnap.id, ...docSnap.data() });
    });
    list.sort((a, b) => {
      if (a.completed !== b.completed) {
        return a.completed ? 1 : -1;
      }
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    });
    callback(list);
  }, (error) => {
    console.error("Error subscribing to tasks:", error);
    callback([], error);
  });
}

export async function addTask(data) {
  const taskData = {
    title: data.title || "",
    oppId: data.oppId || "",
    oppName: data.oppName || "",
    assignedTo: data.assignedTo || "",
    dueDate: data.dueDate || "",
    completed: !!data.completed,
    completedAt: data.completedAt || null,
    createdAt: data.createdAt || new Date().toISOString(),
    weekOf: data.weekOf || getMondayOfDate(),
    type: data.type || "manual"
  };
  const docRef = await addDoc(collection(db, "tasks"), taskData);
  return docRef.id;
}

export async function updateTask(id, data) {
  const docRef = doc(db, "tasks", id);
  await updateDoc(docRef, data);
}

export async function toggleTaskComplete(id, currentCompleted, completedBy = "") {
  const docRef = doc(db, "tasks", id);
  const newStatus = !currentCompleted;
  await updateDoc(docRef, {
    completed: newStatus,
    completedAt: newStatus ? new Date().toISOString() : null,
    completedBy: newStatus ? (completedBy || "Unknown User") : null
  });
}

export async function deleteTask(id) {
  const docRef = doc(db, "tasks", id);
  await deleteDoc(docRef);
}

// Automated weekly touch generation
export async function ensureWeeklyTouchTasks() {
  try {
    const currentMonday = getMondayOfDate();
    const currentSunday = getSundayOfDate();

    // 1. Fetch active opportunities
    const oppsSnapshot = await getDocs(collection(db, "opportunities"));
    const activeOpps = [];
    oppsSnapshot.forEach(docSnap => {
      const opp = { id: docSnap.id, ...docSnap.data() };
      const statusLower = (opp.status || "").trim().toLowerCase();
      const isArchived = opp.archived === true || opp.archived === "true" || statusLower === "archived";
      const isLost = statusLower === "lost";
      if (!isArchived && !isLost) {
        activeOpps.push(opp);
      }
    });

    if (activeOpps.length === 0) return;

    // 2. Fetch existing tasks for this week
    const tasksSnapshot = await getDocs(collection(db, "tasks"));
    const existingOppWeekMap = new Map(); // oppId -> taskDoc
    tasksSnapshot.forEach(docSnap => {
      const t = { id: docSnap.id, ...docSnap.data() };
      if (t.oppId && t.weekOf === currentMonday && t.type === "weekly_touch") {
        existingOppWeekMap.set(t.oppId, t);
      }
    });

    // 3. Create missing weekly touch tasks or sync existing
    for (const opp of activeOpps) {
      const oppLead = opp.assignedTo || opp.owner || opp.createdBy || opp.assignedUser || "";
      const oppTitle = opp.shortDesc || opp.name || "Opportunity";

      if (!existingOppWeekMap.has(opp.id)) {
        await addTask({
          title: `Weekly Touch: ${oppTitle}`,
          oppId: opp.id,
          oppName: oppTitle,
          assignedTo: oppLead,
          dueDate: currentSunday,
          completed: false,
          createdAt: new Date().toISOString(),
          weekOf: currentMonday,
          type: "weekly_touch"
        });
        existingOppWeekMap.set(opp.id, { id: 'created', oppId: opp.id });
      } else {
        const existingTask = existingOppWeekMap.get(opp.id);
        // Sync assignedTo or oppName if it changed/was updated
        if (existingTask.id && (!existingTask.assignedTo && oppLead)) {
          await updateTask(existingTask.id, {
            assignedTo: oppLead,
            oppName: oppTitle
          });
        }
      }
    }
  } catch (error) {
    console.error("Error generating weekly touch tasks:", error);
  }
}
